// The completion-funnel events — a FAITHFUL transcription of the import-engine
// annex's O5 (`docs/initiatives/new/dashboard-import-tool/import-engine-contract.md`
// § "O5 · Funnel events — fixed vocabulary, observational role"), whose metric
// definitions are pinned by SPEC §3.8.
//
// ⚠️ READ THIS BEFORE CHANGING ANYTHING HERE.
//
// THESE EVENTS DO NOT CARRY THE METRIC. `D-FUNNEL-TRANSPORT` (SPEC §3.8,
// decisions.md) settles the split: **the job record is the source of truth for
// the funnel; these events feed PostHog, which observes it** and carries PLG
// metrics. The headline funnel IS the §3.2 state machine — upload succeeded →
// mapping confirmed → validation passed → committed → succeeded maps onto
// `STAGED → MAPPING → VALIDATING → READY → COMMITTING → SUCCESS` — so the ≥85%
// activation metric is computed server-side from job records with no client
// instrumentation at all. If the events and the job record ever disagree, the
// job record is right.
//
// The consequence worth internalizing: **an event that fails to fire costs a
// dashboard, never the ≥85% metric.** Telemetry here inherits the same
// "assists, never gates" rule as the AI layer. Do not add a retry, a queue, or
// a blocking await around emitting one of these.
//
// THAT DOES NOT MAKE THE VOCABULARY OPTIONAL. Annex §8 freezes the seams: the
// six names and their payloads are pinned because a wizard rebuild — or a
// vendor host adapter — must produce a *comparable* series. Drift here silently
// changes what the dashboards are showing even while the underlying metric
// stays correct, which is the worst failure mode available: a wrong number that
// nothing alarms on. Changing a name or a payload field is a SPEC escalation
// (annex §8: a `decisions.md` entry + an annex edit + a SPEC edit), not a
// build-time convenience.
//
// WHO EMITS THESE. Annex §4.3 obligation 3 puts it on the surface: "Emits the
// O5 events with the O5 names." Whoever builds the wizard (Milestone D) emits
// these names with these payloads; a vendor iframe that cannot emit them must
// be wrapped by a host adapter in `packages/dashboard` that does (§4.3
// obligation 4 — a third-party surface sits INSIDE the boundary, behind an
// adapter). The adapter, not the vendor, owes the series.
//
// Transcription discipline, same as `import-job.ts`: no invented fields, no
// renamed fields, no "nicer" shapes. O5's payload table is the field list. If a
// shape here looks wrong, the fix is an annex/SPEC edit first (the
// plan-is-wrong protocol), not a divergence here.

import { z } from 'zod';

import { importJobCountsSchema, importJobStatusSchema } from './import-job.js';

// ---------------------------------------------------------------------------
// O5 — the frozen event vocabulary
// ---------------------------------------------------------------------------

/**
 * O5's six events, one per funnel step, in the annex's own table order (which
 * is also the order a job traverses them).
 *
 * The `import.` prefix is part of each frozen name — PostHog has one flat event
 * namespace shared with every other product surface, so the prefix is what
 * keeps these queryable as a family.
 *
 * Exported as a list, not just an enum, so a producer can iterate the
 * vocabulary (e.g. to register or assert coverage of all six steps) without
 * re-typing the strings and risking a typo that silently produces a
 * seventh-name series nobody's dashboard reads.
 */
export const IMPORT_FUNNEL_EVENT_NAMES = [
  'import.upload_succeeded',
  'import.mapping_proposed',
  'import.mapping_confirmed',
  'import.validation_completed',
  'import.committed',
  'import.job_finished',
] as const;

export const importFunnelEventNameSchema = z.enum(IMPORT_FUNNEL_EVENT_NAMES);
export type ImportFunnelEventName = z.infer<typeof importFunnelEventNameSchema>;

// ---------------------------------------------------------------------------
// O5 — the common envelope
// ---------------------------------------------------------------------------

/**
 * O5: "Every event carries `{ jobId, orgId, actorId, occurredAt }` plus the
 * fields below."
 *
 * `jobId` is the join key back to the authoritative job record — it is what
 * makes an observational event reconcilable against the source of truth, and
 * therefore what makes a dropped-event gap detectable rather than invisible.
 *
 * `occurredAt` is an ISO-8601 datetime string, matching how every timestamp in
 * `import-job.ts` is typed (`createdAt`, `completedAt`, `lastProgressAt`,
 * `writtenAt`, …): `z.string().datetime()`. It is deliberately NOT a
 * client-clock-independent server stamp — these are client-emitted events, so
 * the field records when the step happened on the surface. Any analysis that
 * needs a trustworthy clock reads the job record's own timestamps instead.
 */
export const importFunnelEventEnvelopeSchema = z.object({
  /** The job this step belongs to — the join key back to the §3.1 job record. */
  jobId: z.string(),
  /** The org running the import (§3.1 `orgId`). */
  orgId: z.string(),
  /** Clerk user ID of whoever took the step (§3.1 `actorId`). */
  actorId: z.string(),
  /** ISO-8601 instant the step occurred, per the envelope note above. */
  occurredAt: z.string().datetime(),
});
export type ImportFunnelEventEnvelope = z.infer<
  typeof importFunnelEventEnvelopeSchema
>;

// ---------------------------------------------------------------------------
// O5 — per-event payloads
// ---------------------------------------------------------------------------

/**
 * `import.upload_succeeded` — O5 payload: `fileName`, `fileSizeBytes`,
 * `rowCount`, `parseWarningCodes[]`.
 *
 * Fired after a successful presigned upload, which is exactly the funnel's
 * denominator: SPEC §3.8 — "'Started' means a job exists, which happens only
 * after a successful presigned upload."
 *
 * `parseWarningCodes` values are `ParseWarningCode` from the `ParseProvider`
 * port (annex §4.1) — `ROW_WIDTH_MISMATCH`, `ENCODING_GUESSED`, and the rest.
 * They are kept LOOSELY TYPED as strings here on purpose. Annex §8 makes a new
 * `ParseWarningCode` an explicitly cheap additive change and requires that
 * "consumers must tolerate unknown codes (render them, count them, never crash
 * on them)". Pinning the enum in this contract would invert that: adding a code
 * to the parse port would start failing validation inside an event producer,
 * i.e. an additive parse change would break telemetry emission. A funnel event
 * must never be the thing that rejects a legal parse result.
 */
export const importUploadSucceededEventSchema =
  importFunnelEventEnvelopeSchema.extend({
    name: z.literal('import.upload_succeeded'),
    /** Original uploaded filename, as in §3.1's `fileName`. */
    fileName: z.string(),
    fileSizeBytes: z.number().int().nonnegative(),
    /** Data rows detected by the parse (annex §4.1 `ParsedTable.rowCount`). */
    rowCount: z.number().int().nonnegative(),
    /**
     * `ParseWarningCode` values from annex §4.1, intentionally `string` — see
     * the annex §8 unknown-code rule in this schema's doc comment.
     */
    parseWarningCodes: z.array(z.string()),
  });
export type ImportUploadSucceededEvent = z.infer<
  typeof importUploadSucceededEventSchema
>;

/**
 * `import.mapping_proposed` — O5 payload: `engine`, `fingerprintMatched`,
 * `degraded`, `columnCount`, `autoTierCount`, `reviewTierCount`,
 * `manualTierCount`.
 *
 * This is the "mapping quality" half of SPEC §3.8's product instrumentation:
 * the confidence distribution of AI proposals, plus whether the proposal came
 * from a fingerprint or a degraded (provider-down) path at all.
 *
 * `engine` is a free-form string, not an enum: §5.1's engine internals are
 * explicitly still being prototyped (its banner scopes "behavior is spec-final"
 * to the response shape, not the engine), and O5 does not enumerate the values.
 * Inventing an enum here would pin something the SPEC deliberately left open.
 *
 * `degraded` is the §5.1 provider-degraded manual-mapping fallback — true when
 * the user got a manual grid because the mapping provider was unavailable.
 * Distinguishing that from a genuinely low-confidence proposal is the whole
 * point of carrying it: they look identical in the tier counts and mean
 * completely different things about mapping quality.
 *
 * `fingerprintMatched` corresponds to §5.2 source fingerprinting, which
 * `D-FINGERPRINT-DEFERRAL` parks as Tier 2 of the §4.0.1 ladder — so in V1 this
 * is expected to be uniformly `false`. It stays in the payload because O5
 * freezes it, and because SPEC §3.8 names tier distribution as the signal for
 * "whether the deferred fingerprint layer is worth reviving": a field that is
 * always false is the baseline that revival would be measured against.
 *
 * The three tier counts are the §5.1 confidence bands (`high` → auto,
 * `medium` → review, `low`/unmapped → manual; see `mappingConfidenceTier` in
 * `./mapping.js`), NOT the §4.0.1 header-set tier ladder — the ladder's tiers
 * classify the whole file, while these count columns. They should sum to
 * `columnCount`; that is not enforced here because a validation failure inside
 * a producer would drop the event, which is strictly worse than a slightly
 * wrong dashboard row (the "assists, never gates" rule at the top of this
 * file).
 */
export const importMappingProposedEventSchema =
  importFunnelEventEnvelopeSchema.extend({
    name: z.literal('import.mapping_proposed'),
    /** Which mapping engine produced the proposal. Free-form: §5.1 internals are unpinned. */
    engine: z.string(),
    /** §5.2 fingerprint hit. Expected uniformly false in V1 (D-FINGERPRINT-DEFERRAL). */
    fingerprintMatched: z.boolean(),
    /** §5.1 provider-degraded fallback — the user got a manual grid, not a bad proposal. */
    degraded: z.boolean(),
    /** Source columns in the file (annex §4.1 `ParsedTable.headers.length`). */
    columnCount: z.number().int().nonnegative(),
    /** §5.1 high-confidence band (≥0.9) — auto-mapped. */
    autoTierCount: z.number().int().nonnegative(),
    /** §5.1 medium band (0.5–0.9) — mapped but highlighted for review. */
    reviewTierCount: z.number().int().nonnegative(),
    /** §5.1 low-confidence-or-unmapped band — user must choose manually. */
    manualTierCount: z.number().int().nonnegative(),
  });
export type ImportMappingProposedEvent = z.infer<
  typeof importMappingProposedEventSchema
>;

/**
 * `import.mapping_confirmed` — O5 payload: `overriddenCount`,
 * `customFieldCount`, `skippedCount`.
 *
 * SPEC §3.8's mapping-quality metrics: "% of proposals the user overrides" and
 * "% of columns left unmapped/skipped".
 *
 * NOTE ON REDUNDANCY, which is deliberate. The override rate is *also*
 * computable from the durable job record — annex O3 obligation 3 makes `origin`
 * required on every `ColumnMapping` "precisely so the funnel's mapping-override
 * rate (§3.8) [is] computable from the durable job record rather than from
 * UI-only telemetry". This event is the PostHog mirror of a number the job
 * record already owns, which is exactly the D-FUNNEL-TRANSPORT arrangement. On
 * a disagreement, O3's `origin` fields win.
 *
 * `skippedCount` counts `target: "SKIP"` mappings, which O3 obligation 1
 * requires be explicit rather than absent — so this is a count of recorded
 * decisions, not of missing entries.
 */
export const importMappingConfirmedEventSchema =
  importFunnelEventEnvelopeSchema.extend({
    name: z.literal('import.mapping_confirmed'),
    /** Columns whose confirmed target differs from what the engine proposed (O3 `origin`). */
    overriddenCount: z.number().int().nonnegative(),
    /** Columns mapped to a custom contact field rather than a standard one. */
    customFieldCount: z.number().int().nonnegative(),
    /** Columns confirmed as an explicit `target: "SKIP"` (O3 obligation 1). */
    skippedCount: z.number().int().nonnegative(),
  });
export type ImportMappingConfirmedEvent = z.infer<
  typeof importMappingConfirmedEventSchema
>;

/**
 * `import.validation_completed` — O5 payload: "the O4 counts, flattened".
 *
 * FLATTENED ON PURPOSE, AND ONLY HERE. Annex O4's `TriageSummary` nests the
 * four consent counts under `consent: { optedIn, optedOut, needsReview, noData }`.
 * This event hoists them to `consentOptedIn` / `consentOptedOut` /
 * `consentNeedsReview` / `consentNoData` for analytics ergonomics: PostHog
 * property filters and breakdowns operate on flat top-level properties, and a
 * nested object becomes an opaque JSON blob you cannot break down or threshold
 * on. That is a telemetry-transport concern, not a modelling opinion.
 *
 * **O4 — not this event — is the authority on these numbers**, and the same
 * goes for the `triage` map persisted on the job record (§3.1), which is where
 * SPEC §3.8 says triage-category rates durably live. This is a mirror. If the
 * flattened copy and O4 disagree, O4 is right.
 *
 * O4's non-count members are deliberately absent: `issues[]` (an unbounded
 * array of per-code detail, with `sampleRowNumbers` that are row-level and
 * unhelpful as event properties) and `needsAttentionFileKey` (an S3 key —
 * infrastructure, not a metric). O5 says "the O4 counts", and only the counts.
 *
 * O4's stated obligations — `total === ready + excluded` and
 * `ready === willCreate + willMerge` — are NOT enforced as a Zod refinement
 * here. O4 requires a mismatch surface "as a visible error state, never a
 * silently wrong number"; that surfacing belongs to the validate step and the
 * Hub, which can render it. A refinement in this schema would instead make the
 * producer throw and DROP the event, converting a visible data-quality signal
 * into a silent hole in a dashboard.
 */
export const importValidationCompletedEventSchema =
  importFunnelEventEnvelopeSchema.extend({
    name: z.literal('import.validation_completed'),
    total: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    /** O4: rows with ≥1 auto-fix warning; a subset of `ready`. */
    autoFixed: z.number().int().nonnegative(),
    /** O4: subset of `ready` requiring a human decision before commit (blocks COMMIT while >0). */
    needsAttention: z.number().int().nonnegative(),
    duplicatesCollapsed: z.number().int().nonnegative(),
    /** O4: of `ready`, rows where no existing contact matches. */
    willCreate: z.number().int().nonnegative(),
    /** O4: of `ready`, rows whose email matches an existing contact. */
    willMerge: z.number().int().nonnegative(),

    // --- O4's `consent` map, flattened (see the doc comment above) -----------

    /** O4 `consent.optedIn`. */
    consentOptedIn: z.number().int().nonnegative(),
    /** O4 `consent.optedOut`. */
    consentOptedOut: z.number().int().nonnegative(),
    /** O4 `consent.needsReview`. */
    consentNeedsReview: z.number().int().nonnegative(),
    /** O4 `consent.noData`. */
    consentNoData: z.number().int().nonnegative(),
  });
export type ImportValidationCompletedEvent = z.infer<
  typeof importValidationCompletedEventSchema
>;

/**
 * `import.committed` — O5 payload: `total`, `willCreate`, `willMerge`,
 * `attested`.
 *
 * Fired at the §4.4 commit, i.e. at the funnel's last user-present step. The
 * three counts are the O4 numbers the user actually confirmed (they can differ
 * from `import.validation_completed` if the user re-validated in between), and
 * `attested` is §3.1's `consentAttestation.attested`.
 *
 * `attested: false` IS A VALID OUTCOME, not a failure — §9.1 and §3.1 are
 * explicit that declining the attestation still imports. Any dashboard built on
 * this event must not treat `attested: false` as a drop-off; it is a recorded
 * decision on a completed commit.
 */
export const importCommittedEventSchema =
  importFunnelEventEnvelopeSchema.extend({
    name: z.literal('import.committed'),
    total: z.number().int().nonnegative(),
    willCreate: z.number().int().nonnegative(),
    willMerge: z.number().int().nonnegative(),
    /** §3.1 `consentAttestation.attested`. False is a valid recorded outcome (§9.1). */
    attested: z.boolean(),
  });
export type ImportCommittedEvent = z.infer<typeof importCommittedEventSchema>;

/**
 * `import.job_finished` — O5 payload: terminal `status`, final `counts`,
 * `durationMs`.
 *
 * This one DOES reuse the job record's own types rather than restating them:
 * `status` is `importJobStatusSchema` (§3.2's frozen 13-state enum) and `counts`
 * is `importJobCountsSchema` (§3.1's `{ total, created, merged, excluded,
 * errored }`). Both already live in this same contract package and are stable,
 * so re-declaring them would create a second definition that could silently
 * drift from the state machine — the opposite of the `parseWarningCodes` case
 * above, where the enum lives behind a port whose additive evolution must not
 * be able to break a producer.
 *
 * Note `counts` stays NESTED here even though `validation_completed` flattens
 * O4's. Different reasons: the flattening above buys PostHog breakdowns on
 * counts nobody else types, whereas `importJobCountsSchema` is a shared shape
 * whose identity with §3.1 is the point. If a dashboard needs flat count
 * properties, flatten at the emit site; do not fork the schema.
 *
 * "Terminal" in O5's sense is the job-has-stopped sense, so SUCCESS qualifies —
 * use `isImportJobSettled()` from `./import-job.js`, NOT
 * `IMPORT_JOB_TERMINAL_STATUSES` (which excludes SUCCESS because §3.2 defines
 * `SUCCESS → REVERSING`). The status is typed as the full enum rather than a
 * narrowed terminal subset deliberately: narrowing would make a producer throw
 * — and therefore drop the event — on any status the subset guessed wrong,
 * including the FAILED_NEEDS_CLEANUP and CLEANED_UP paths.
 *
 * `durationMs` is wall-clock from job creation (§3.1 `createdAt`) to finish
 * (§3.1 `completedAt`). It spans user think-time in the wizard, so it is a
 * time-to-value measure, not a compute-cost one.
 */
export const importJobFinishedEventSchema =
  importFunnelEventEnvelopeSchema.extend({
    name: z.literal('import.job_finished'),
    /** §3.2's status enum — see the terminal-vs-settled note above. */
    status: importJobStatusSchema,
    /** §3.1's `counts` map, reused rather than restated. */
    counts: importJobCountsSchema,
    /** Wall-clock `createdAt` → `completedAt`, in milliseconds. */
    durationMs: z.number().int().nonnegative(),
  });
export type ImportJobFinishedEvent = z.infer<
  typeof importJobFinishedEventSchema
>;

// ---------------------------------------------------------------------------
// O5 — the union
// ---------------------------------------------------------------------------

/**
 * The whole O5 vocabulary as one discriminated union on `name`.
 *
 * A discriminated union (not a loose `{ name, properties }` bag) is what makes
 * the freeze mechanically checkable: a producer emitting
 * `import.mapping_confirmed` cannot compile while omitting `overriddenCount`,
 * and a consumer switching on `name` gets exhaustiveness checking across all
 * six steps. That is the cheapest available defence against the drift annex §8
 * warns about — a payload that quietly changes shape and takes a dashboard's
 * meaning with it.
 *
 * Each variant is also exported individually above, so a producer that only
 * ever emits one step can validate against exactly that schema instead of
 * parsing against the union.
 */
export const importFunnelEventSchema = z.discriminatedUnion('name', [
  importUploadSucceededEventSchema,
  importMappingProposedEventSchema,
  importMappingConfirmedEventSchema,
  importValidationCompletedEventSchema,
  importCommittedEventSchema,
  importJobFinishedEventSchema,
]);
export type ImportFunnelEvent = z.infer<typeof importFunnelEventSchema>;
